import React from 'react';
import { Gray_Dark_030, Static_White } from '@croquiscom/zds';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { useScrollLock } from '@common/hooks';
import { web_max_width } from '@common/styles';

export interface DrawerProps {
  colorScheme?: 'light' | 'dark';
  open: boolean;
  pure_children?: boolean;
  useScroll?: boolean;
  height?: string;
  max_height?: string;
  onClose: () => void;
}

export const Drawer: React.FC<React.PropsWithChildren<DrawerProps>> = ({
  children,
  colorScheme,
  open,
  pure_children = false,
  useScroll = false,
  height = '520px',
  max_height,
  onClose,
}) => {
  const { setContainerRef, is_drawer_visible, is_container_visible, handleTransitionEnd } = useScrollLock(open);
  const isDarkTheme = useTheme().name === 'dark' && colorScheme !== 'light';

  return (
    <Container is_visible={is_container_visible} role='dialog' aria-modal>
      <Dimmed is_visible={is_drawer_visible} onClick={onClose} data-allow-preview />
      <DrawerWrapper
        isDarkTheme={isDarkTheme}
        ref={setContainerRef}
        is_visible={is_drawer_visible}
        useScroll={useScroll}
        $height={height}
        max_height={max_height}
        onTransitionEnd={handleTransitionEnd}
      >
        {is_container_visible && (!pure_children ? <ContentWrapper>{children}</ContentWrapper> : children)}
      </DrawerWrapper>
    </Container>
  );
};

const Container = styled.div<{ is_visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  width: 100%;
  height: 100%;
  visibility: ${({ is_visible }) => (is_visible ? 'visible' : 'hidden')};
`;

const Dimmed = styled.div<{ is_visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  opacity: ${({ is_visible }) => (is_visible ? 1 : 0)};
  background: rgba(73, 80, 87, 0.7);
  transition: opacity 0.2s ease;
`;

const DrawerWrapper = styled.div<{
  is_visible: boolean;
  $height: string;
  useScroll?: boolean;
  max_height?: string;
  isDarkTheme: boolean;
}>`
  position: absolute;
  left: 50%;
  bottom: 0;
  z-index: 100;
  width: 100%;
  max-width: ${web_max_width}px;
  height: ${({ $height }) => $height};
  max-height: ${({ max_height }) => max_height || '90%'};
  border-radius: 8px 8px 0 0;
  ${({ useScroll }) => (useScroll ? 'overflow-y: scroll' : 'overflow: hidden;')};
  background-color: ${({ isDarkTheme }) => (isDarkTheme ? Gray_Dark_030 : Static_White)};
  transform: translate(-50%, ${({ is_visible }) => (is_visible ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const ContentWrapper = styled.div`
  height: 100%;
  height: calc(100% - env(safe-area-inset-bottom));
`;
